/**
 * 発売日の判定（日本時間基準）。
 *
 * 日付だけの文字列（YYYY-MM-DD）は「日本時間のその日の 00:00」として解釈する。
 * これにより、UTC 解釈による 9 時間ズレで「今日」が翌日扱いになる問題を防ぐ。
 */

export const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
export const DAY_MS = 24 * 60 * 60 * 1000;

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 発売日文字列をミリ秒へ。日付のみは日本時間の 00:00 として扱う。 */
export function parseReleaseTime(value: string): number {
  if (!value) return 0;
  const trimmed = value.trim();
  const match = DATE_ONLY_PATTERN.exec(trimmed);
  if (match) {
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) - JST_OFFSET_MS;
  }
  const time = Date.parse(trimmed);
  return Number.isNaN(time) ? 0 : time;
}

/** その瞬間が属する日本時間の日の 00:00（ミリ秒） */
export function startOfJstDay(value: Date): number {
  const shifted = value.getTime() + JST_OFFSET_MS;
  const dayStart = Math.floor(shifted / DAY_MS) * DAY_MS;
  return dayStart - JST_OFFSET_MS;
}

/**
 * 基準日から見た発売日の日数差。
 * 0 = 今日、-1 = 明日（発売予定）、1 = 昨日。
 */
export function releaseDayDiff(dateString: string, reference: Date): number | null {
  const time = parseReleaseTime(dateString);
  if (!time) return null;
  const target = startOfJstDay(new Date(time));
  return Math.round((startOfJstDay(reference) - target) / DAY_MS);
}

/** 発売済み（今日を含む）か */
export function isReleased(dateString: string, reference: Date): boolean {
  const diff = releaseDayDiff(dateString, reference);
  return diff !== null && diff >= 0;
}

/** 発売予定（明日以降）か */
export function isUpcoming(dateString: string, reference: Date): boolean {
  const diff = releaseDayDiff(dateString, reference);
  return diff !== null && diff < 0;
}

/**
 * 直近N日以内に発売された作品か。
 * 未来の日付（翌日発売など）は含めない。当日は含める。
 */
export function withinReleasedDays(dateString: string, days: number, reference: Date): boolean {
  const diff = releaseDayDiff(dateString, reference);
  if (diff === null) return false;
  return diff >= 0 && diff <= days;
}